// AdminSystemConfigPage - Configuration système (numérotation, limites fichiers, etc.)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Settings, Hash, FileUp, Save, RefreshCw, Info, 
  FileText, Users, Gavel, AlertTriangle, HardDrive
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Variables disponibles pour les formats de numérotation
const NUMBERING_VARIABLES = [
  { var: '{YEAR}', desc: 'Année en cours (4 chiffres)', example: '2025' },
  { var: '{YEAR_SHORT}', desc: 'Année courte (2 chiffres)', example: '25' },
  { var: '{MONTH}', desc: 'Mois (2 chiffres)', example: '02' },
  { var: '{DAY}', desc: 'Jour (2 chiffres)', example: '07' },
  { var: '{SEQ}', desc: 'Numéro séquentiel', example: '001' },
  { var: '{SEQ4}', desc: 'Séquence 4 chiffres', example: '0001' },
  { var: '{SEQ5}', desc: 'Séquence 5 chiffres', example: '00001' },
  { var: '{PROVINCE}', desc: 'Code province', example: 'KIN' },
  { var: '{JURISDICTION}', desc: 'Code juridiction', example: 'TGI' },
  { var: '{TYPE}', desc: 'Type de document', example: 'RP' },
  { var: '{RANDOM}', desc: 'Code aléatoire (6 car.)', example: 'A3X9K2' }
];

const AdminSystemConfigPage = () => {
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    // Numérotation des dossiers
    case_numbering: {
      format: '{TYPE}/{PROVINCE}/{YEAR}/{SEQ4}',
      prefix: 'RP',
      start_sequence: 1,
      reset_yearly: true,
      reset_monthly: false
    },
    // Numérotation des procès
    trial_numbering: {
      format: 'PROC/{JURISDICTION}/{YEAR}/{SEQ4}',
      prefix: 'PROC',
      start_sequence: 1,
      reset_yearly: true
    },
    // Numérotation des plaintes
    complaint_numbering: {
      format: 'PLT/{PROVINCE}/{YEAR}/{MONTH}/{SEQ4}',
      prefix: 'PLT',
      start_sequence: 1,
      reset_monthly: true
    },
    // Numérotation des détenus
    prisoner_numbering: {
      format: 'DET/{PROVINCE}/{YEAR}/{SEQ5}',
      prefix: 'DET',
      start_sequence: 1,
      reset_yearly: true
    },
    // Limites de fichiers
    file_limits: {
      max_file_size_mb: 100,
      max_total_size_mb: 1024,
      max_files_per_entity: 20,
      allowed_extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'mp3', 'wav', 'mp4', 'avi', 'mov'],
      chunk_size_mb: 5
    },
    // Options générales
    general: {
      require_unji_for_persons: false,
      auto_fill_person_info: true,
      enable_biometric_verification: false
    }
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/system-config');
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.post('/admin/system-config', config);
      toast.success(language === 'fr' ? 'Configuration enregistrée' : 'Configuration saved');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Preview numbering format
  const previewFormat = (format, prefix) => {
    const now = new Date();
    let preview = format
      .replace('{YEAR}', now.getFullYear())
      .replace('{YEAR_SHORT}', String(now.getFullYear()).slice(-2))
      .replace('{MONTH}', String(now.getMonth() + 1).padStart(2, '0'))
      .replace('{DAY}', String(now.getDate()).padStart(2, '0'))
      .replace('{SEQ}', '001')
      .replace('{SEQ4}', '0001')
      .replace('{SEQ5}', '00001')
      .replace('{PROVINCE}', 'KIN')
      .replace('{JURISDICTION}', 'TGI')
      .replace('{TYPE}', prefix || 'DOC')
      .replace('{RANDOM}', 'A3X9K2');
    return preview;
  };

  // Numbering config component
  const NumberingConfig = ({ title, icon: Icon, section, config: sectionConfig, updateFn }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>{language === 'fr' ? 'Format de numérotation' : 'Numbering format'}</Label>
          <Input
            value={sectionConfig.format}
            onChange={(e) => updateFn(section, 'format', e.target.value)}
            placeholder="{TYPE}/{YEAR}/{SEQ4}"
            className="font-mono"
          />
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-muted-foreground">{language === 'fr' ? 'Aperçu:' : 'Preview:'}</span>
            <Badge variant="outline" className="font-mono">
              {previewFormat(sectionConfig.format, sectionConfig.prefix)}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Préfixe par défaut' : 'Default prefix'}</Label>
            <Input
              value={sectionConfig.prefix}
              onChange={(e) => updateFn(section, 'prefix', e.target.value)}
              placeholder="RP"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Démarrer à' : 'Start at'}</Label>
            <Input
              type="number"
              min="1"
              value={sectionConfig.start_sequence}
              onChange={(e) => updateFn(section, 'start_sequence', parseInt(e.target.value) || 1)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>{language === 'fr' ? 'Réinitialiser annuellement' : 'Reset yearly'}</Label>
            <p className="text-xs text-muted-foreground">
              {language === 'fr' ? 'La séquence repart à 1 chaque année' : 'Sequence resets to 1 each year'}
            </p>
          </div>
          <Switch
            checked={sectionConfig.reset_yearly}
            onCheckedChange={(checked) => updateFn(section, 'reset_yearly', checked)}
          />
        </div>

        {section === 'complaint_numbering' && (
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{language === 'fr' ? 'Réinitialiser mensuellement' : 'Reset monthly'}</Label>
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? 'La séquence repart à 1 chaque mois' : 'Sequence resets to 1 each month'}
              </p>
            </div>
            <Switch
              checked={sectionConfig.reset_monthly}
              onCheckedChange={(checked) => updateFn(section, 'reset_monthly', checked)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-system-config">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {language === 'fr' ? 'Configuration Système' : 'System Configuration'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Numérotation automatique, limites de fichiers et options générales' 
                : 'Auto-numbering, file limits and general options'}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'fr' ? 'Enregistrer' : 'Save'}
          </Button>
        </div>

        <Tabs defaultValue="numbering" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="numbering" className="gap-2">
              <Hash className="w-4 h-4" />
              {language === 'fr' ? 'Numérotation' : 'Numbering'}
            </TabsTrigger>
            <TabsTrigger value="files" className="gap-2">
              <FileUp className="w-4 h-4" />
              {language === 'fr' ? 'Fichiers' : 'Files'}
            </TabsTrigger>
            <TabsTrigger value="general" className="gap-2">
              <Settings className="w-4 h-4" />
              {language === 'fr' ? 'Général' : 'General'}
            </TabsTrigger>
          </TabsList>

          {/* Numbering Tab */}
          <TabsContent value="numbering" className="space-y-6">
            {/* Variables reference */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="w-5 h-5" />
                  {language === 'fr' ? 'Variables disponibles' : 'Available variables'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {NUMBERING_VARIABLES.map((v) => (
                    <div key={v.var} className="p-2 bg-muted rounded-lg">
                      <code className="text-xs font-bold text-primary">{v.var}</code>
                      <p className="text-xs text-muted-foreground mt-1">{v.desc}</p>
                      <p className="text-xs mt-1">Ex: <span className="font-mono">{v.example}</span></p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <NumberingConfig
                title={language === 'fr' ? 'Dossiers judiciaires' : 'Legal cases'}
                icon={FileText}
                section="case_numbering"
                config={config.case_numbering}
                updateFn={updateConfig}
              />
              
              <NumberingConfig
                title={language === 'fr' ? 'Procès' : 'Trials'}
                icon={Gavel}
                section="trial_numbering"
                config={config.trial_numbering}
                updateFn={updateConfig}
              />
              
              <NumberingConfig
                title={language === 'fr' ? 'Plaintes' : 'Complaints'}
                icon={AlertTriangle}
                section="complaint_numbering"
                config={config.complaint_numbering}
                updateFn={updateConfig}
              />
              
              <NumberingConfig
                title={language === 'fr' ? 'Détenus' : 'Prisoners'}
                icon={Users}
                section="prisoner_numbering"
                config={config.prisoner_numbering}
                updateFn={updateConfig}
              />
            </div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HardDrive className="w-5 h-5" />
                  {language === 'fr' ? 'Limites de fichiers' : 'File limits'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez les limites de taille et types de fichiers autorisés'
                    : 'Configure size limits and allowed file types'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Taille max par fichier (MB)' : 'Max file size (MB)'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5000"
                      value={config.file_limits.max_file_size_mb}
                      onChange={(e) => updateConfig('file_limits', 'max_file_size_mb', parseInt(e.target.value) || 100)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Jusqu\'à 5 GB supporté' : 'Up to 5 GB supported'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Taille totale max (MB)' : 'Max total size (MB)'}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={config.file_limits.max_total_size_mb}
                      onChange={(e) => updateConfig('file_limits', 'max_total_size_mb', parseInt(e.target.value) || 1024)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Par dossier/plainte' : 'Per case/complaint'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Fichiers max par élément' : 'Max files per item'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={config.file_limits.max_files_per_entity}
                      onChange={(e) => updateConfig('file_limits', 'max_files_per_entity', parseInt(e.target.value) || 20)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Taille des chunks (MB)' : 'Chunk size (MB)'}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={config.file_limits.chunk_size_mb}
                    onChange={(e) => updateConfig('file_limits', 'chunk_size_mb', parseInt(e.target.value) || 5)}
                    className="max-w-[200px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Pour le téléchargement de gros fichiers par morceaux'
                      : 'For uploading large files in chunks'}
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Extensions autorisées' : 'Allowed extensions'}</Label>
                  <Textarea
                    value={config.file_limits.allowed_extensions.join(', ')}
                    onChange={(e) => updateConfig('file_limits', 'allowed_extensions', 
                      e.target.value.split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean)
                    )}
                    placeholder="pdf, doc, docx, jpg, png, mp3, mp4..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Séparez par des virgules' : 'Separate with commas'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {config.file_limits.allowed_extensions.map((ext) => (
                    <Badge key={ext} variant="secondary">.{ext}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Options générales' : 'General options'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{language === 'fr' ? 'UNJI obligatoire pour les personnes' : 'Require UNJI for persons'}</Label>
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Exiger l\'identifiant national lors de la création d\'une personne'
                        : 'Require national ID when creating a person'}
                    </p>
                  </div>
                  <Switch
                    checked={config.general.require_unji_for_persons}
                    onCheckedChange={(checked) => updateConfig('general', 'require_unji_for_persons', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{language === 'fr' ? 'Remplissage automatique' : 'Auto-fill person info'}</Label>
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Remplir automatiquement les infos (téléphone, adresse) quand une personne est sélectionnée'
                        : 'Auto-fill info (phone, address) when a person is selected'}
                    </p>
                  </div>
                  <Switch
                    checked={config.general.auto_fill_person_info}
                    onCheckedChange={(checked) => updateConfig('general', 'auto_fill_person_info', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{language === 'fr' ? 'Vérification biométrique' : 'Biometric verification'}</Label>
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Activer la vérification par empreintes/scan rétinien'
                        : 'Enable fingerprint/retinal scan verification'}
                    </p>
                  </div>
                  <Switch
                    checked={config.general.enable_biometric_verification}
                    onCheckedChange={(checked) => updateConfig('general', 'enable_biometric_verification', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminSystemConfigPage;
